/**
 * The author's equipment proposal in the review (plan v21 B4, docs/plans/2026-10-13_import-author-knows-the-plant.md).
 *
 * A draft's author_notes.equipment (B3) names, for each equipment key of its manifest, an item the plant already has,
 * or none with why none fits: the author never invents one. Once the plant's items are loaded, the review starts each
 * picker from, in order:
 *   1. the binding the reader already has, when that item is in the list (a new version keeps its equipment);
 *   2. the author's item, when it is in the list;
 *   3. the item the key's name clearly points to (hub suggestEntity), only when the author proposed nothing for it.
 * The reviewer's own choice is over all three. Each picker says where its value came from, and why, in plain words.
 * Until the items are loaded nothing is prefilled and nothing is said about them: an empty list proves nothing. The
 * review sheet reads every item of the plant, page by page, so a binding is never judged against a cut list.
 */
import { streamEquipmentKeys, suggestEntity, type BindOption, type Bindings, type ManifestStream } from "./hub";
import { own } from "./own";

export { own };

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/** The author's answer for one equipment key: the plant's item, or null for "none fits", and why. */
export interface EquipmentProposal {
  role: string;
  entityId: string | null;
  why: string;
}

/** The author's proposals by equipment key (its role), each once; an entry that names no role, or an item id that is
 *  not a string, is left out. A draft from before B3 has none. */
export function equipmentProposalsOf(notes: unknown): Record<string, EquipmentProposal> {
  const out: Record<string, EquipmentProposal> = {};
  const raw = isObj(notes) ? notes.equipment : undefined;
  for (const e of Array.isArray(raw) ? raw : []) {
    if (!isObj(e)) continue;
    const role = text(e.role);
    if (!role || own(out, role)) continue;
    const id = e.entity_id;
    if (id !== null && (typeof id !== "string" || id.trim() === "")) continue;
    out[role] = { role, entityId: id === null ? null : id.trim(), why: text(e.why) };
  }
  return out;
}

/** Where a picker's starting value came from; "gone" is a reader's binding to an item not in the list, which starts
 *  nothing when neither the author nor the names fill the key. */
export type PrefillSource = "earlier" | "author" | "names" | "gone";

export interface Prefill {
  bindings: Bindings;
  /** By the manifest's stream key, then the equipment key: where each starting value came from. */
  source: Record<string, Record<string, PrefillSource>>;
}

/** Every picker's starting value, in the order the module's comment gives, from the plant's loaded items. A key the
 *  author answered with none, or with an item not in the list, gets no guess from the names over that answer. */
export function prefillBindings(
  streams: readonly ManifestStream[],
  earlier: Bindings,
  proposals: Readonly<Record<string, EquipmentProposal>>,
  items: readonly Pick<BindOption, "id" | "name" | "kind" | "templateId">[],
): Prefill {
  const ids = new Set(items.map((e) => e.id));
  const out: Prefill = { bindings: {}, source: {} };
  const mark = (stream: string, eq: string, from: PrefillSource) => {
    out.source[stream] = { ...(own(out.source, stream) ?? {}), [eq]: from };
  };
  const set = (stream: string, eq: string, id: string, from: PrefillSource) => {
    out.bindings[stream] = { ...(own(out.bindings, stream) ?? {}), [eq]: id };
    mark(stream, eq, from);
  };
  for (const s of streams) {
    for (const eq of streamEquipmentKeys(s)) {
      const prior = own(own(earlier, s.key), eq);
      if (prior && ids.has(prior)) {
        set(s.key, eq, prior, "earlier");
        continue;
      }
      const p = own(proposals, eq);
      if (p) {
        if (p.entityId && ids.has(p.entityId)) set(s.key, eq, p.entityId, "author");
        else if (prior) mark(s.key, eq, "gone");
        continue;
      }
      const guess = suggestEntity(eq, items);
      if (guess) set(s.key, eq, guess, "names");
      else if (prior) mark(s.key, eq, "gone");
    }
  }
  return out;
}

/** Where the plant's items stand in the review: the pickers start, and say anything, only once they are ready. */
export type ItemsState = "loading" | "ready" | "failed";

const NOTHING: Prefill = { bindings: {}, source: {} };

/** The pickers' starting values: prefillBindings once the plant's items are ready, else nothing (an empty or failed
 *  list would read as every item gone, and Approve waits, since nothing is bound). */
export function startingBindings(
  items: ItemsState,
  streams: readonly ManifestStream[],
  earlier: Bindings,
  proposals: Readonly<Record<string, EquipmentProposal>>,
  entities: readonly Pick<BindOption, "id" | "name" | "kind" | "templateId">[],
): Prefill {
  return items === "ready" ? prefillBindings(streams, earlier, proposals, entities) : NOTHING;
}

/** What the approval binds: the prefill, under the reviewer's own picks (a pick back to "Choose", "", included). */
export function chosenBindings(prefill: Bindings, picked: Bindings): Bindings {
  const merged: Bindings = {};
  for (const [k, b] of Object.entries(prefill)) merged[k] = { ...b };
  for (const [k, b] of Object.entries(picked)) merged[k] = { ...(own(merged, k) ?? {}), ...b };
  return merged;
}

/** What a picker says under it. `addFromCatalog` asks for the link to the catalog (the process graph's add dialog). */
export interface BindNote {
  tone: "info" | "warning" | "muted";
  text: string;
  addFromCatalog?: boolean;
}

/** The process graph, whose add dialog adds an item from the catalog. */
export const CATALOG_HREF = "/app/twin";

/**
 * The note under one picker, once the plant's items are loaded. `source` is where its value started (undefined when
 * nothing prefilled it); `picked` is whether the reviewer chose it themselves, after which only a disagreement with
 * the author is still said. An item id not among `items` is "not in the list below", never "gone from the plant": the
 * list may be cut at its page size.
 */
export function bindNote(args: {
  value: string;
  source?: PrefillSource;
  picked: boolean;
  proposal?: EquipmentProposal;
  items: readonly Pick<BindOption, "id" | "name">[];
}): BindNote | null {
  const { value, source, picked, proposal, items } = args;
  const nameOf = (id: string) => items.find((e) => e.id === id)?.name ?? "";
  // ": why." (a sentence's own end kept), or "." when the author gave no reason.
  const because = (why: string) => (why ? `: ${/[.!?]$/.test(why) ? why : `${why}.`}` : ".");
  const proposed = proposal?.entityId ? nameOf(proposal.entityId) : "";

  if (picked) {
    if (!proposal || proposal.entityId === value) return null;
    if (proposal.entityId === null) return { tone: "muted", text: `The brain found no item that fits${because(proposal.why)}` };
    return proposed ? { tone: "muted", text: `The brain proposed ${proposed}${because(proposal.why)}` } : null;
  }
  if (source === "earlier") {
    if (proposal && proposal.entityId !== value) {
      if (proposal.entityId === null) {
        return { tone: "warning", text: `Kept from the reader in use, but the brain found no item that fits${because(proposal.why)}` };
      }
      if (proposed) {
        return { tone: "warning", text: `Kept from the reader in use, but the brain proposed ${proposed}${because(proposal.why)}` };
      }
    }
    return { tone: "muted", text: "Kept from the reader in use." };
  }
  if (source === "author") {
    return { tone: "info", text: `Proposed by the brain${proposal?.why ? because(proposal.why) : ": check it is the right item."}` };
  }
  if (source === "names") return { tone: "info", text: "Suggested from the names: check it is the right item." };
  if (value !== "") return null;
  if (proposal?.entityId === null) {
    return {
      tone: "warning",
      text:
        `The brain found no item of your plant that fits${because(proposal.why)} Add it from the catalog on the ` +
        "process graph, then open this review again: the choices made here are not kept.",
      addFromCatalog: true,
    };
  }
  if (proposal?.entityId) {
    return { tone: "warning", text: "The brain proposed an item that is not in the list below: choose the right one." };
  }
  if (source === "gone") {
    return { tone: "warning", text: "The reader in use was bound to an item that is not in the list below: choose the right one." };
  }
  return null;
}
